import { useCallback, useEffect, useRef, useState } from 'react'
import FloatingCart from '@/components/ui/FloatingCart'
import type { MenuPage, Product } from '@/lib/types'
import { fmt, isAvailable, minPrice } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

// Category emoji fallbacks
const defaultCatIcons: Record<string, string> = {
  COMBODEAL: '🎁',
  BUFFÉ: '🍜',
  LUNCH: '🍱',
  FÖRRÄTTER: '🥟',
  SUSHI: '🍣',
  VARMRÄTTER: '🍛',
  WOK: '🥘',
  NUDLAR: '🍜',
  ROLLS: '🍣',
  BOWLS: '🥗',
  DESSERT: '🍨',
  DRYCKER: '🥤',
  'LÄSK & VATTEN': '🥤',
  'ÖL & SAKE': '🍺',
  VIN: '🍷',
  'ASIATISKA DRYCKER': '🧋',
  'NIGIRI & SASHIMI': '🍣',
  'DIM SUM': '🥟',
  TILLBEHÖR: '🍚',
  KAFFE: '☕',
}

function getPageImage(page: MenuPage): string | null {
  for (const c of page.categories) {
    for (const p of c.products) {
      if (p.properties?.imageUrl && isAvailable(p)) return p.properties.imageUrl
    }
  }
  return null
}

export default function ProductGrid() {
  const pages = useKioskStore((s) => s.pages)
  const setSelectedProduct = useKioskStore((s) => s.setSelectedProduct)
  const setStep = useKioskStore((s) => s.setStep)
  const loading = useKioskStore((s) => s.loading)
  const locale = useKioskStore((s) => s.locale)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const setCurrentCategoryLabel = useKioskStore((s) => s.setCurrentCategoryLabel)
  const setBackAction = useKioskStore((s) => s.setBackAction)
  const isLandscape = remoteConfig?.layoutMode === 'landscape'

  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Two-level nav: null = category grid, string = show products in that category/page.
  // Om backoffice-konfig har skipCategoryGrid=true så hoppar vi rakt in på
  // "HELA MENYN"-vyn (alla produkter samlade utan kategori-mellansida).
  const skipCategoryGrid = remoteConfig?.skipCategoryGrid === true
  const [viewingPage, setViewingPage] = useState<string | null>(skipCategoryGrid ? '__all__' : null)
  const [selectedCatFilter, setSelectedCatFilter] = useState<string | null>(null)
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({})

  const sv = locale === 'sv'

  // Sync currentCategoryLabel + backAction to the landscape header based on current sub-view
  const setAfterProductAddedAction = useKioskStore((s) => s.setAfterProductAddedAction)

  useEffect(() => {
    if (viewingPage) {
      // Inside a page — show its label
      const pageLabel = viewingPage === '__all__' ? (sv ? 'HELA MENYN' : 'FULL MENU') : (pages.find((p) => p.id === viewingPage)?.name || '').toUpperCase()
      setCurrentCategoryLabel(pageLabel)
      if (skipCategoryGrid && viewingPage === '__all__') {
        // Back-knappen → menuTypeSelect (det enda meningsfulla "tillbaka" eftersom
        // kategorigrid:en är avstängd). Post-add-flow → ingen navigering, user
        // stannar i HELA MENYN och kan fortsätta beställa.
        setBackAction(() => {
          setStep('menuTypeSelect')
          useKioskStore.getState().clearCart()
        })
        setAfterProductAddedAction(() => {
          /* no-op: stanna kvar i HELA MENYN */
        })
      } else {
        // Vanligt läge: back-knappen + post-add går båda till kategorigrid:en
        const goToCategoryGrid = () => {
          setViewingPage(null)
          setSelectedCatFilter(null)
        }
        setBackAction(goToCategoryGrid)
        setAfterProductAddedAction(goToCategoryGrid)
      }
    } else {
      // On category grid — title is "VÄLJ KATEGORI"; back goes to menuTypeSelect (clears cart)
      setCurrentCategoryLabel(sv ? 'VÄLJ KATEGORI' : 'CHOOSE CATEGORY')
      setBackAction(() => {
        setStep('menuTypeSelect')
        useKioskStore.getState().clearCart()
      })
      setAfterProductAddedAction(() => {}) // Adding from this screen must preserve the cart.
    }
    return () => {
      setCurrentCategoryLabel(null)
      setBackAction(null)
      setAfterProductAddedAction(null)
    }
  }, [viewingPage, pages, sv, skipCategoryGrid, setCurrentCategoryLabel, setBackAction, setAfterProductAddedAction, setStep])

  const handleProductPress = (product: Product) => {
    if (!isAvailable(product)) return
    setSelectedProduct(product)
  }

  // Scroll-hint: "there's more below" — updates when scrollRef scrolls or content changes.
  // Hides the arrow as soon as the bottom row is ≥50% visible and detects actual column
  // count from the DOM (so arrows align with real grid gaps, not just the global setting).
  const [hintState, setHintState] = useState<{
    hasMoreBelow: boolean
    cols: number
    gapPx: number
  }>({
    hasMoreBelow: false,
    cols: 3,
    gapPx: 48,
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: Rebind observers when loading replaces the scroll area or category content changes.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      const tiles = el.querySelectorAll('.kiosk-btn')
      const last = tiles[tiles.length - 1] as HTMLElement | undefined
      const tileH = last?.offsetHeight ?? 300
      const threshold = tileH / 2
      const more = el.scrollHeight - el.scrollTop - el.clientHeight > threshold

      // Detect actual column count + gap from the last grid in view
      let cols = 1
      let gapPx = 0
      const parent = last?.parentElement
      if (parent) {
        const style = getComputedStyle(parent)
        const tpl = style.gridTemplateColumns
        if (tpl && tpl !== 'none') {
          cols = tpl.split(/\s+/).filter((s) => s.length > 0).length
        }
        const gap = parseFloat(style.columnGap || style.gap || '0')
        if (Number.isFinite(gap)) gapPx = gap
      }
      setHintState({ hasMoreBelow: more, cols, gapPx })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [loading, viewingPage, pages, expandedCats, selectedCatFilter])
  const { hasMoreBelow, cols: hintCols, gapPx: hintGap } = hintState

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-kiosk-bg">
        <div className="kiosk-spinner" />
      </div>
    )
  }

  // Currently viewing a specific page's products
  // For __all__, create a virtual page with all categories from all pages
  const activePage = viewingPage === '__all__' ? { id: '__all__', name: 'HELA MENYN', categories: pages.flatMap((p) => p.categories) } : pages.find((p) => p.id === viewingPage)

  if (activePage) {
    const allCats = activePage.categories.filter((c) => c.products.some(isAvailable))
    const visibleCats = selectedCatFilter ? allCats.filter((c) => c.id === selectedCatFilter) : allCats

    // Visa sticky kategoribar i HELA MENYN — aktiveras via backoffice-toggle
    // (fullMenuCategoryBar). Skipasdig om det bara finns en kategori (då är
    // baren onödig).
    const showCategoryBar = remoteConfig?.fullMenuCategoryBar === true && viewingPage === '__all__' && allCats.length > 1
    const categoryBarAlign = remoteConfig?.fullMenuCategoryBarAlign === 'center' ? 'center' : 'left'

    return (
      <div className="h-full w-full flex flex-col bg-kiosk-bg relative">
        {/* Sticky kategoribar — bara i HELA MENYN-läge när toggle är på */}
        {showCategoryBar && <CategoryBar categories={allCats.map((c) => ({ id: c.id, name: c.name }))} scrollRef={scrollRef} align={categoryBarAlign} />}
        {viewingPage !== '__all__' && pages.filter((page) => page.categories.some((category) => category.products.some(isAvailable))).length > 1 && (
          <CategoryShortcutBar
            pages={pages.filter((page) => page.categories.some((category) => category.products.some(isAvailable)))}
            activeId={activePage.id}
            locale={locale}
            onSelect={(id) => {
              setViewingPage(id)
              setSelectedCatFilter(null)
              scrollToTop()
            }}
          />
        )}

        {/* Products with category separators + collapse */}
        {/* pt-3 i porträtt ger andrum så ring-2 på översta rutorna inte klipps av scroll-kanten */}
        <div ref={scrollRef} className={`flex-1 overflow-y-auto kiosk-scroll pb-28 ${isLandscape ? 'px-14 pt-10 max-w-350 w-full mx-auto' : 'px-5 pt-3'}`}>
          {visibleCats.map((cat) => {
            const products = cat.products.filter(isAvailable)
            if (products.length === 0) return null
            const isExpanded = expandedCats[cat.id] || false
            const effectiveLayout = remoteConfig?.defaultProductLayout || 'card-image-2col'
            const wantsThreeCol = effectiveLayout.includes('3col') || viewingPage === '__all__'
            // Smart col logic: use 3 cols only if products fill rows nicely
            // Same column behavior in landscape and portrait — landscape just gets more breathing room
            const useThreeCol = wantsThreeCol && (products.length % 3 === 0 || products.length >= 5)
            const colCount = useThreeCol ? 3 : 2
            const cols = colCount === 3 ? 'grid-cols-3' : 'grid-cols-2'
            const isListLayout = effectiveLayout === 'list-row' || effectiveLayout === 'text-only'
            const maxRows = remoteConfig?.maxCollapsedRows ?? 1
            // For list layouts, each row = 1 item; for grids, each row = colCount items
            const collapsedCount = isListLayout ? maxRows * 3 : colCount * maxRows
            const collapseEnabled = remoteConfig?.collapseEnabled ?? true
            const showCollapse = collapseEnabled && products.length > collapsedCount
            const visibleProducts = showCollapse && !isExpanded ? products.slice(0, collapsedCount) : products
            const hasHiddenProducts = showCollapse && !isExpanded && products.length > collapsedCount
            // Peek: show partial next row (clipped) to hint there's more
            const peekProducts = hasHiddenProducts ? products.slice(collapsedCount, collapsedCount + colCount) : []
            const catLabel = cat.name

            return (
              <div key={cat.id} id={`cat-section-${cat.id}`} data-category-id={cat.id} className="mb-2 scroll-mt-24">
                {/* Category separator — like Mister York */}
                {allCats.length > 1 && (
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-kiosk-muted/40" />
                    <h2 className="text-kiosk-text font-bold text-lg tracking-wider shrink-0 uppercase">{catLabel}</h2>
                    <div className="flex-1 h-px bg-kiosk-muted/40" />
                  </div>
                )}
                {isListLayout ? (
                  /* List layouts — vertical stack, full width */
                  <div className="flex flex-col gap-2">
                    {visibleProducts.map((product) =>
                      effectiveLayout === 'text-only' ? (
                        <ProductTextRow key={product.id} product={product} onPress={() => handleProductPress(product)} />
                      ) : (
                        <ProductListRow key={product.id} product={product} onPress={() => handleProductPress(product)} />
                      ),
                    )}
                  </div>
                ) : (
                  /* Grid layouts — card-image or card-compact */
                  <div className={`grid ${cols} ${isLandscape ? 'gap-12' : 'gap-5'}`}>
                    {visibleProducts.map((product) => (
                      <ProductCard key={product.id} product={product} onPress={() => handleProductPress(product)} compact={effectiveLayout.includes('compact')} />
                    ))}
                  </div>
                )}
                {/* Peek row — partially visible next row with gradient fade */}
                {peekProducts.length > 0 && (
                  <div
                    className="relative mt-3 overflow-hidden"
                    style={{
                      maxHeight: isListLayout ? '60px' : useThreeCol ? '180px' : '280px',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
                      maskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
                    }}
                  >
                    {isListLayout ? (
                      <div className="flex flex-col gap-2 pointer-events-none">
                        {peekProducts.map((product) =>
                          effectiveLayout === 'text-only' ? (
                            <ProductTextRow key={product.id} product={product} onPress={() => {}} />
                          ) : (
                            <ProductListRow key={product.id} product={product} onPress={() => {}} />
                          ),
                        )}
                      </div>
                    ) : (
                      <div className={`grid ${cols} ${isLandscape ? 'gap-12' : 'gap-5'} pointer-events-none`}>
                        {peekProducts.map((product) => (
                          <ProductCard key={product.id} product={product} onPress={() => {}} compact={effectiveLayout.includes('compact')} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* "Visa alla X i kategori" — like Mister York */}
                {showCollapse && !isExpanded && (
                  <div className="flex justify-center mt-3 relative z-10 -mb-4">
                    <button
                      onClick={() => setExpandedCats((prev) => ({ ...prev, [cat.id]: true }))}
                      className="kiosk-btn px-6 py-3 rounded-full bg-kiosk-surface border-2 border-kiosk-accent text-kiosk-accent text-sm font-bold flex items-center gap-2 active:bg-kiosk-surface-2 shadow-lg"
                    >
                      {sv ? `Visa alla ${products.length} i ${catLabel}` : `Show all ${products.length} in ${catLabel}`}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
                {showCollapse && isExpanded && (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={() => setExpandedCats((prev) => ({ ...prev, [cat.id]: false }))}
                      className="kiosk-btn px-5 py-2.5 rounded-full border border-kiosk-muted/40 text-kiosk-muted text-xs font-medium flex items-center gap-2 active:bg-kiosk-surface-2"
                    >
                      {sv ? 'Visa mindre' : 'Show less'}
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Scroll hint: bouncing chevron(s) when there's more content below — placed in the gaps between tiles */}
        {hasMoreBelow && <ScrollHint cols={hintCols} gapPx={hintGap} isLandscape={isLandscape} />}

        <FloatingCart />
      </div>
    )
  }

  // ─── Category grid view ───
  // Category columns: 3 default (2 or 4 if configured). Same in portrait and landscape —
  // landscape just gives each tile more breathing room.
  const configuredCols = remoteConfig?.categoryColumns
  const catGridCols = configuredCols === 2 ? 'grid-cols-2' : configuredCols === 4 ? 'grid-cols-4' : 'grid-cols-3'

  return (
    <div className="h-full w-full flex flex-col bg-kiosk-bg relative">
      {/* Orange VÄLJ KATEGORI bar removed — header shows title + back in both modes */}

      {/* Category grid — columns configurable per till */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto kiosk-scroll pb-28 ${isLandscape ? 'px-14 pt-10 max-w-350 w-full mx-auto' : 'px-5 pt-3'}`}>
        <div className={`grid ${isLandscape ? 'gap-12' : 'gap-5'} ${catGridCols}`}>
          {/* Full-menu button with a collage of category images */}
          <CategoryTile
            label={sv ? 'HELA MENYN' : 'FULL MENU'}
            imageUrl={null}
            collageImages={pages.slice(0, 4).map(getPageImage).filter(Boolean) as string[]}
            icon="🍽️"
            count={pages.reduce((s, p) => s + p.categories.reduce((sc, c) => sc + c.products.filter(isAvailable).length, 0), 0)}
            locale={locale}
            onPress={() => {
              setViewingPage('__all__')
              scrollToTop()
            }}
          />
          {pages.map((page, index) => {
            const productCount = page.categories.reduce((s, c) => s + c.products.filter(isAvailable).length, 0)
            if (productCount === 0) return null
            const pageUpper = page.name.toUpperCase()
            const img = getPageImage(page)
            const icon = defaultCatIcons[pageUpper] || '🍽️'
            return (
              <CategoryTile
                key={page.id}
                eager={index < 6}
                label={page.name}
                imageUrl={img}
                icon={icon}
                count={productCount}
                locale={locale}
                onPress={() => {
                  setViewingPage(page.id)
                  scrollToTop()
                }}
              />
            )
          })}
        </div>
      </div>

      {/* Scroll hint: bouncing chevron(s) when there's more content below — placed in the gaps between tiles */}
      {hasMoreBelow && <ScrollHint cols={hintCols} gapPx={hintGap} isLandscape={isLandscape} />}

      <FloatingCart />
    </div>
  )
}

// ─── Scroll hint: bouncing chevrons in the gaps between tiles ───
// Reads actual column count + gap from the DOM so arrows always align with real gutters:
// 1 col / list layout → 1 centered arrow
// N cols (N ≥ 2)      → N-1 arrows, one in each gutter between tiles
function ScrollHint({ cols, gapPx, isLandscape }: { cols: number; gapPx: number; isLandscape?: boolean }) {
  const halfGap = gapPx / 2
  // 1-col / list layouts have no gutters — show a single centered arrow
  if (cols < 2) {
    return (
      <div className="absolute inset-x-0 bottom-28 pointer-events-none z-30 flex justify-center">
        <ScrollHintArrow />
      </div>
    )
  }
  const gridClass = cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : cols === 4 ? 'grid-cols-4' : `grid-cols-${cols}`
  return (
    <div className="absolute inset-x-0 bottom-28 pointer-events-none z-30">
      <div className={isLandscape ? 'px-14 max-w-350 mx-auto' : 'px-5'}>
        <div className={`grid ${gridClass} relative`} style={{ gap: `${gapPx}px` }}>
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} className="relative h-10">
              {i < cols - 1 && (
                <div className="absolute top-0 translate-x-1/2" style={{ right: -halfGap }}>
                  <ScrollHintArrow />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ScrollHintArrow() {
  return (
    <div className="w-10 h-10 rounded-full bg-kiosk-surface/95 ring-2 ring-white/30 shadow-lg shadow-black/40 flex items-center justify-center animate-bounce">
      <svg className="w-6 h-6 text-kiosk-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  )
}

function CategoryShortcutBar({ pages, activeId, locale, onSelect }: { pages: MenuPage[]; activeId: string; locale: string; onSelect: (id: string) => void }) {
  const activeButton = useRef<HTMLButtonElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: The active button ref follows the selected page.
  useEffect(() => {
    activeButton.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeId])
  return (
    <nav aria-label={locale === 'sv' ? 'Kategorier' : 'Categories'} className="shrink-0 overflow-x-auto kiosk-scroll border-b border-kiosk-surface-2 px-5 py-3">
      <div className="flex gap-3 w-max mx-auto">
        {pages.map((page) => {
          const imageUrl = getPageImage(page)
          const active = page.id === activeId
          return (
            <button
              key={page.id}
              ref={active ? activeButton : undefined}
              type="button"
              onClick={() => onSelect(page.id)}
              aria-current={active ? 'page' : undefined}
              className={`kiosk-btn w-32 rounded-xl overflow-hidden border-2 ${active ? 'border-kiosk-accent bg-kiosk-accent/10' : 'border-kiosk-surface-2 bg-kiosk-surface'}`}
            >
              {imageUrl ? <img src={imageUrl} alt="" className="w-full h-16 object-cover" loading="lazy" /> : <div className="h-16 flex items-center justify-center text-3xl">🍽</div>}
              <span className={`block px-2 py-2 text-sm font-bold leading-tight ${active ? 'text-kiosk-accent' : 'text-kiosk-text'}`}>{page.name}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

function CategoryTile({
  label,
  imageUrl,
  collageImages,
  eager = false,
  icon,
  count,
  locale,
  onPress,
}: {
  label: string
  imageUrl: string | null
  collageImages?: string[]
  eager?: boolean
  icon: string
  count: number
  locale: string
  onPress: () => void
}) {
  const sv = locale === 'sv'
  return (
    <button
      onClick={onPress}
      className="kiosk-btn bg-kiosk-surface rounded-2xl overflow-hidden text-center group ring-2 ring-white/25 shadow-lg shadow-black/40 hover:ring-white/40 hover:shadow-xl hover:shadow-black/50 active:shadow-md transition-all"
    >
      <div className="aspect-4/3 overflow-hidden relative">
        {collageImages && collageImages.length >= 4 ? (
          <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
            {collageImages.slice(0, 4).map((img, i) => (
              <img
                key={i}
                src={img}
                alt=""
                className="w-full h-full object-cover group-active:scale-105 transition-transform"
                loading={eager ? 'eager' : 'lazy'}
                fetchPriority={eager ? 'high' : 'auto'}
              />
            ))}
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            className="w-full h-full object-cover group-active:scale-105 transition-transform"
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-kiosk-surface-2 to-kiosk-surface flex items-center justify-center text-5xl">{icon}</div>
        )}
        <div className="absolute inset-0 bg-linear-to-t from-kiosk-surface/90 via-transparent to-transparent" />
      </div>
      <div className="p-4">
        <p className="text-kiosk-text text-2xl font-bold leading-tight">{label}</p>
        <p className="text-kiosk-muted text-base mt-1">
          {count} {sv ? 'rätter' : 'dishes'}
        </p>
      </div>
    </button>
  )
}

// ─── Product card (card-image layout — default) ───
function ProductCard({ product, onPress, compact }: { product: Product; onPress: () => void; compact?: boolean }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button
      onClick={onPress}
      className="kiosk-btn bg-kiosk-surface rounded-2xl overflow-hidden text-left flex flex-col ring-2 ring-white/25 shadow-lg shadow-black/40 hover:ring-white/40 hover:shadow-xl hover:shadow-black/50 active:shadow-md transition-all"
    >
      {props.imageUrl ? (
        <div className={`${compact ? 'aspect-4/3' : 'aspect-4/3'} w-full overflow-hidden bg-kiosk-surface-2`}>
          <img src={props.imageUrl} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className={`${compact ? 'aspect-3/2' : 'aspect-4/3'} w-full bg-kiosk-surface-2 flex items-center justify-center`}>
          <span className={compact ? 'text-3xl' : 'text-4xl'}>🍽</span>
        </div>
      )}
      <div className={`${compact ? 'p-2.5' : 'p-4'} flex-1 flex flex-col`}>
        {/* Name + price on same row: name left, price right */}
        <div className="flex items-start justify-between gap-3">
          <p className={`text-kiosk-text font-bold ${compact ? 'text-sm' : 'text-lg'} leading-tight line-clamp-2 flex-1 min-w-0`}>{product.name}</p>
          <div className={`shrink-0 whitespace-nowrap flex flex-col items-end leading-tight`}>
            {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && (
              <span className={`text-kiosk-muted line-through ${compact ? 'text-xs' : 'text-sm'}`}>{fmt(props.preCampaignPrice)}</span>
            )}
            <p className={`text-kiosk-accent font-bold ${compact ? 'text-sm' : 'text-lg'}`}>
              {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
              {fmt(props.price || minPrice(product))}
            </p>
          </div>
        </div>
        {!compact && props.description && <p className="text-kiosk-muted text-xs mt-2 line-clamp-2">{props.description}</p>}
      </div>
    </button>
  )
}

// ─── Product list row (horizontal row with small image) ───
function ProductListRow({ product, onPress }: { product: Product; onPress: () => void }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button onClick={onPress} className="kiosk-btn bg-kiosk-surface rounded-xl overflow-hidden text-left flex flex-row items-center gap-3 p-3 w-full">
      {props.imageUrl ? (
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-kiosk-surface-2">
          <img src={props.imageUrl} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="w-20 h-20 shrink-0 rounded-lg bg-kiosk-surface-2 flex items-center justify-center">
          <span className="text-2xl">🍽</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-kiosk-text font-semibold text-sm leading-tight line-clamp-2">{product.name}</p>
        {props.description && <p className="text-kiosk-muted text-xs mt-1 line-clamp-1">{props.description}</p>}
      </div>
      <div className="shrink-0 pl-2 flex flex-col items-end leading-tight">
        {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-xs">{fmt(props.preCampaignPrice)}</span>}
        <p className="text-kiosk-accent font-bold text-base">
          {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
          {fmt(props.price || minPrice(product))}
        </p>
      </div>
    </button>
  )
}

// ─── Product text only (name + price, no image) ───
function ProductTextRow({ product, onPress }: { product: Product; onPress: () => void }) {
  const locale = useKioskStore((s) => s.locale)
  const sv = locale === 'sv'
  if (product.properties == null) return null
  const props = product.properties
  const hasAlts = props.alternativeGroups && props.alternativeGroups.length > 0
  return (
    <button onClick={onPress} className="kiosk-btn bg-kiosk-surface rounded-xl text-left flex flex-row items-center justify-between px-4 py-3.5 w-full">
      <div className="flex-1 min-w-0 pr-3">
        <p className="text-kiosk-text font-semibold text-sm leading-tight line-clamp-1">{product.name}</p>
        {props.description && <p className="text-kiosk-muted text-xs mt-0.5 line-clamp-1">{props.description}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end leading-tight">
        {props.preCampaignPrice && props.price && props.preCampaignPrice > props.price && <span className="text-kiosk-muted line-through text-xs">{fmt(props.preCampaignPrice)}</span>}
        <p className="text-kiosk-accent font-bold text-sm">
          {hasAlts || !props.price ? (sv ? 'Från ' : 'From ') : ''}
          {fmt(props.price || minPrice(product))}
        </p>
      </div>
    </button>
  )
}

/* ───── Sticky kategoribar för HELA MENYN ─────
 *
 * Horisontell scroll-bar med en knapp per kategori. Klick → smooth-scroll
 * till motsvarande sektion (id=`cat-section-{id}`). Aktiv kategori spåras
 * via IntersectionObserver och highlightas. När scroll rör sig är bar:en
 * också scrollad så aktiv knapp syns.
 */
function CategoryBar({ categories, scrollRef, align = 'left' }: { categories: { id: string; name: string }[]; scrollRef: React.RefObject<HTMLDivElement | null>; align?: 'left' | 'center' }) {
  const [activeId, setActiveId] = useState<string>(categories[0]?.id ?? '')
  const barRef = useRef<HTMLDivElement>(null)

  // IntersectionObserver för att detektera vilken kategori som syns mest just nu
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const sectionIds = categories.map((c) => `cat-section-${c.id}`)
    const elements = sectionIds.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => el !== null)
    if (elements.length === 0) return

    // Aktivt fönster = övre 20% av scroll-area:n
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-category-id')
          if (id) setActiveId(id)
        }
      },
      {
        root,
        rootMargin: '-10% 0px -70% 0px',
        threshold: 0,
      },
    )
    elements.forEach((el) => {
      observer.observe(el)
    })
    return () => observer.disconnect()
  }, [categories, scrollRef])

  // Auto-scroll baren så aktiv knapp syns när användaren bläddrar
  useEffect(() => {
    if (!activeId) return
    const btn = barRef.current?.querySelector<HTMLButtonElement>(`[data-cat-btn="${activeId}"]`)
    btn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [activeId])

  function handleClick(id: string) {
    const target = document.getElementById(`cat-section-${id}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Center-läget: använd inline-flex + mx-auto. När innehållet får plats
  // centreras det. Vid overflow blir det auto-vänsterställt (browser-default
  // för flex-children som är bredare än container) — scroll fungerar fortfarande.
  return (
    <div
      ref={barRef}
      className="shrink-0 border-b border-kiosk-muted/20 bg-kiosk-bg overflow-x-auto kiosk-scroll"
      style={{ scrollbarWidth: 'none', textAlign: align === 'center' ? 'center' : 'left' }}
    >
      <div className={`${align === 'center' ? 'inline-flex' : 'flex'} items-stretch gap-1 px-3 py-2 whitespace-nowrap`}>
        {categories.map((c) => {
          const isActive = activeId === c.id
          return (
            <button
              key={c.id}
              type="button"
              data-cat-btn={c.id}
              onClick={() => handleClick(c.id)}
              className={`kiosk-btn shrink-0 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                isActive ? 'bg-kiosk-accent text-white shadow' : 'bg-kiosk-surface text-kiosk-text hover:bg-kiosk-surface-2'
              }`}
            >
              {c.name.toUpperCase()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
